import React from 'react';
import {
  defineMessages,
  injectIntl,
  FormattedMessage,
  type IntlShape,
} from 'react-intl';
import { omit } from 'lodash';
import Vote from 'brastrap/common/vote/Vote';
import Rating from 'brastrap/common/rating/Rating';
import Grid from 'brastrap/helpers/l-grid/l-grid';
import Swatch from 'brastrap/promotional/swatch/Swatch';

const messages = defineMessages({
  purchased: {
    id: 'response-review.stylePurchased',
    defaultMessage: 'Colour: ',
  },
  shopVisit: {
    id: 'response-review.shop-visit',
    defaultMessage:
      '{customerName} visited on {visitDate} and received a fitting from {fitter}.',
  },
  shopName: {
    id: 'response-review.shop-name',
    defaultMessage: 'Bravissimo {shopName}',
  },
  reviewedBy: {
    id: 'response-review.reviewed-by',
    defaultMessage: 'Reviewed by',
  },
  responseFrom: {
    id: 'response-review.response-from',
    defaultMessage: 'Response from',
  },
  wasThisHelpful: {
    id: 'response-review.was-this-helpful',
    defaultMessage: 'Was this helpful?',
  },
  customerRating: {
    id: 'response-review.customer-rating',
    defaultMessage: 'Customer rating:',
  },
  sizeUK: {
    id: 'response-review.size-uk',
    defaultMessage: 'UK Size {size}',
  },
  sizeUS: {
    id: 'response-review.size-us',
    defaultMessage: 'US Size {size}',
  },
});

// @LOCALE TODO: Locale should be added from respective lang file rather than adding each locale in each lang file,
// if this is not possible then we would need to retrieve this information from the database
const getLocalisedSize = (locale, size, localiseSize, formatMessage) => {
  if (localiseSize) {
    switch (locale) {
      case 'en_GB':
        return formatMessage(messages.sizeUK, { size });
      case 'en_US':
        return formatMessage(messages.sizeUS, { size });
      default:
        return size;
    }
  }
  return size;
};

type Props = CustomerResponse & { intl: IntlShape };

const Response = (props: Props) => {
  const {
    author,
    answer,
    review,
    intl: { formatDate, formatMessage },
    showVote,
    locale,
    localiseSize,
    scores,
    shopName,
    preview,
  } = props;

  const authorName = author.name.split('.')[0];
  const authorData = omit(author, 'data');
  authorData.data = [];
  author.data.forEach(keyValuePair => {
    if (keyValuePair.key === 'Size') {
      authorData.data.push({
        ...keyValuePair,
        value: getLocalisedSize(
          locale,
          keyValuePair.value,
          localiseSize,
          formatMessage
        ),
      });
    } else {
      authorData.data.push(keyValuePair);
    }
  });

  let reviewHeader = null;
  let reviewDetails = null;

  if (review) {
    if (review.colour) {
      reviewDetails = (
        <div className="c-response__reviewDetailsContainer">
          <div className="c-response__detail">
            <FormattedMessage {...messages.purchased} />
            <span className="c-response__style">
              {review.colour.swatch && <Swatch colour={review.colour.swatch} />}
              {review.colour.colour && (
                <span className="c-response__colour">
                  {review.colour.colour}
                </span>
              )}
            </span>
          </div>
          {authorData && authorData.data && authorData.data[1].value && (
            <div className="c-response__detail">
              <h2>Size: </h2>
              <span className="c-response__style">
                {authorData.data[1].value}
              </span>
            </div>
          )}
          {scores && scores.comfort && (
            <div id="review-comfort-rating" className="c-response__detail">
              <h2>Comfort: </h2>
              <span className="c-response__style">{scores.comfort}/5</span>
            </div>
          )}
        </div>
      );
    }
    reviewHeader = (
      <header className="c-response__header">
        {!!shopName && (
          <span className="c-response__subtitle__shop-name">
            <FormattedMessage
              {...messages.shopName}
              values={{
                shopName,
              }}
            />
          </span>
        )}
        <h1 className="c-response__title">
          {review.title}
          <Rating {...review.rating} />
        </h1>
        {reviewDetails}
      </header>
    );
  }

  let vote = null;
  if (showVote && author) {
    vote = (
      <div className="c-response__actions">
        <Vote
          title={formatMessage(messages.wasThisHelpful)}
          yes={review ? review.helpfulCount : answer.helpfulCount}
          no={review ? review.unhelpfulCount : answer.unhelpfulCount}
          onClickYes={() => {}}
          onClickNo={() => {}}
        />
      </div>
    );
  }

  return (
    <article className="c-response">
      <Grid>
        <Grid.Unit modifiers={['content']}>
          {reviewHeader}
          <div className="c-response__reviewContentContainer">
            <div className="c-response__reviewContent">
              {preview && <div className="c-response__overlay" />}
              <div className="c-response__reviewDetails">
                Review by {authorName} on&nbsp;
                <time dateTime={review.date}>
                  {formatDate(review.date, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>{' '}
              </div>
              {review ? review.content : answer.content}
            </div>
          </div>

          {vote}
        </Grid.Unit>
      </Grid>
    </article>
  );
};

export default injectIntl(Response);
